import { directSupabase } from '../../lib/supabaseClient';
import { downloadDriveItemContent } from '../../utils/graphService';
import type { OrderAcceptanceFile } from '../../types/project';
import type {
  AssignableProfile,
  JobAssignee,
  JobCardImage,
  JobCardRecord,
  JobComment,
  JobCompletionEvent,
  JobCardOrderAcceptanceFile,
  OrderAcceptanceCopy,
  SubcontractorAccount,
} from './jobCard.types';
import {
  JOB_CARD_IMAGES_BUCKET,
  JOB_CARD_IMAGE_MAX_COUNT,
  isAllowedJobCardImage,
  jobCardImageStoragePath,
  sanitizeJobCardFileName,
  type JobCardImageKind,
} from './jobCardImages';
import { emitActivityEventBackground } from '../../services/activityEvents.service';
import { emitJobCardNotificationBackground } from '../../services/jobNotifications';
import { assembleAssignedJobs, latestInstallEventsByProject } from './fitterJobList';
import type { AssignedJob, CompletionRow, InstallEventRow } from './fitterJobList';
import { toInstallDateOnly } from './jobCardInstallDate';

const OA_BUCKET = 'order-acceptance';
const OA_REDACTED_BUCKET = 'order-acceptance-redacted';
const OA_CORE_COLUMNS = ['id', 'project_id', 'drive_item_id', 'name', 'storage_path', 'content_type', 'copied_at'];
const OA_REDACT_COLUMNS = ['redacted_name', 'redacted_storage_path', 'redacted_content_type', 'redacted_at'];

function table(name: string) {
  return (directSupabase as any).from(name);
}

function storageSafeName(name: string) {
  return name.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 180) || 'file';
}

type ProfileBits = {
  full_name: string | null;
  email: string | null;
  role: string | null;
};

async function profilesByIds(ids: string[]): Promise<Map<string, ProfileBits>> {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  const map = new Map<string, ProfileBits>();
  if (unique.length === 0) return map;
  const { data, error } = await directSupabase
    .from('profiles')
    .select('id, full_name, email, role')
    .in('id', unique);
  if (error) throw new Error(error.message);
  ((data || []) as Array<{ id: string } & ProfileBits>).forEach((row) => {
    map.set(row.id, { full_name: row.full_name, email: row.email, role: row.role });
  });
  return map;
}

export async function listAssignableCrew(): Promise<AssignableProfile[]> {
  const { data, error } = await directSupabase
    .from('profiles')
    .select('id, full_name, email, role')
    .eq('group', 'staff')
    .eq('is_active', true)
    .in('role', ['fitter', 'labourer', 'admin'])
    .order('full_name', { ascending: true });
  if (error) throw new Error(error.message);
  return (data || []) as AssignableProfile[];
}

const JOB_CARD_CORE_COLUMNS = ['id', 'project_id', 'title', 'created_at', 'created_by'];
const JOB_CARD_OPTIONAL_COLUMNS = ['survey_notes', 'install_date_override', 'install_end_date_override', 'calendar_event_id', 'follows_booking'];
const INSTALL_OVERRIDE_COLUMNS = ['install_date_override', 'install_end_date_override', 'follows_booking'];

type JobCardPatch = Partial<Pick<JobCardRecord, 'title' | 'survey_notes' | 'install_date_override' | 'install_end_date_override' | 'calendar_event_id'>>;

function asJobCard(row: Partial<JobCardRecord> & { id: string; project_id: string; title: string; created_at: string }): JobCardRecord {
  return {
    id: row.id,
    project_id: row.project_id,
    title: row.title,
    survey_notes: typeof row.survey_notes === 'string' ? row.survey_notes : '',
    install_date_override: toInstallDateOnly(row.install_date_override),
    install_end_date_override: toInstallDateOnly(row.install_end_date_override),
    calendar_event_id: typeof row.calendar_event_id === 'string' && row.calendar_event_id ? row.calendar_event_id : null,
    follows_booking: row.follows_booking !== false,
    created_at: row.created_at,
    created_by: row.created_by ?? null,
  };
}

function withPatch(card: JobCardRecord, patch: JobCardPatch): JobCardRecord {
  return {
    ...card,
    survey_notes: patch.survey_notes ?? card.survey_notes,
    install_date_override: 'install_date_override' in patch
      ? toInstallDateOnly(patch.install_date_override)
      : card.install_date_override,
    install_end_date_override: 'install_end_date_override' in patch
      ? toInstallDateOnly(patch.install_end_date_override)
      : card.install_end_date_override,
    calendar_event_id: 'calendar_event_id' in patch ? patch.calendar_event_id ?? null : card.calendar_event_id,
  };
}

function missingColumnIn(message: string | undefined, columns: string[]): string | null {
  if (!message) return null;
  return columns.find((column) => new RegExp(`\\b${column}\\b`, 'i').test(message)) || null;
}

/** Runs a query, dropping optional columns the database does not have yet (unapplied migrations). */
async function withOptionalColumns<T>(
  optional: string[],
  run: (present: string[]) => PromiseLike<{ data: T; error: { message: string } | null }>,
): Promise<{ data: T; present: string[] }> {
  let present = [...optional];
  for (;;) {
    const result = await run(present);
    if (!result.error) return { data: result.data, present };
    const missing = missingColumnIn(result.error.message, present);
    if (!missing) throw new Error(result.error.message);
    present = present.filter((column) => column !== missing);
  }
}

function jobCardColumns(present: string[]) {
  return [...JOB_CARD_CORE_COLUMNS, ...present].join(', ');
}

async function selectJobCardRows(
  run: (columns: string) => PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<unknown[]> {
  const { data } = await withOptionalColumns(JOB_CARD_OPTIONAL_COLUMNS, (present) => run(jobCardColumns(present)));
  return Array.isArray(data) ? data : (data ? [data] : []);
}

export async function listProjectJobCards(projectId: string): Promise<JobCardRecord[]> {
  const rows = await selectJobCardRows((columns) => table('project_job_cards')
    .select(columns)
    .eq('project_id', projectId)
    .order('created_at', { ascending: true }));
  return (rows as JobCardRecord[]).map(asJobCard);
}

export async function getJobCard(jobCardId: string): Promise<JobCardRecord | null> {
  const rows = await selectJobCardRows((columns) => table('project_job_cards')
    .select(columns)
    .eq('id', jobCardId)
    .maybeSingle());
  return rows[0] ? asJobCard(rows[0] as JobCardRecord) : null;
}

/** `followsBooking` false for cards after job card 1: they start with no date. */
export async function createJobCard(
  projectId: string,
  title: string,
  createdBy: string,
  options: { followsBooking?: boolean } = {},
): Promise<JobCardRecord> {
  const trimmed = title.trim() || 'Job card';
  const { data } = await withOptionalColumns(JOB_CARD_OPTIONAL_COLUMNS, (present) => table('project_job_cards')
    .insert({
      project_id: projectId,
      title: trimmed,
      created_by: createdBy,
      ...(present.includes('survey_notes') ? { survey_notes: '' } : {}),
      ...(present.includes('follows_booking') && options.followsBooking === false ? { follows_booking: false } : {}),
    })
    .select(jobCardColumns(present))
    .single());
  return asJobCard(data as JobCardRecord);
}

export async function updateJobCard(jobCardId: string, patch: JobCardPatch): Promise<JobCardRecord> {
  const { data } = await withOptionalColumns(JOB_CARD_OPTIONAL_COLUMNS, (present) => {
    const body = Object.fromEntries(Object.entries(patch).filter(([key]) => (
      !JOB_CARD_OPTIONAL_COLUMNS.includes(key) || present.includes(key)
    )));
    const columns = jobCardColumns(present);
    const query = Object.keys(body).length
      ? table('project_job_cards').update(body).eq('id', jobCardId).select(columns)
      : table('project_job_cards').select(columns).eq('id', jobCardId);
    return query.single();
  });
  if (!data) throw new Error('Job card not found');
  return withPatch(asJobCard(data as JobCardRecord), patch);
}

export async function deleteJobCard(jobCardId: string): Promise<void> {
  const images = await listJobCardImages(jobCardId).catch(() => [] as JobCardImage[]);
  if (images.length) {
    await directSupabase.storage.from(JOB_CARD_IMAGES_BUCKET).remove(images.map((row) => row.storage_path));
  }
  const { error } = await table('project_job_cards').delete().eq('id', jobCardId);
  if (error) throw new Error(error.message);
}

export async function resolveJobCardFromRouteId(id: string): Promise<JobCardRecord | 'ambiguous' | null> {
  const direct = await getJobCard(id);
  if (direct) return direct;
  const cards = await listProjectJobCards(id);
  if (cards.length === 1) return cards[0];
  if (cards.length > 1) return 'ambiguous';
  return null;
}

export function listJobAssignees(jobCardId: string): Promise<JobAssignee[]> {
  return listAssigneesWhere('job_card_id', jobCardId);
}

export function listProjectJobAssignees(projectId: string): Promise<JobAssignee[]> {
  return listAssigneesWhere('project_id', projectId);
}

export async function loadLatestInstallEvent(projectId: string): Promise<InstallEventRow | null> {
  const { data, error } = await directSupabase
    .from('project_events')
    .select('project_id, event_date, end_date, metadata, status, created_at')
    .eq('project_id', projectId)
    .eq('event_type', 'installationDateConfirmation');
  if (error) throw new Error(error.message);
  return latestInstallEventsByProject((data || []) as unknown as InstallEventRow[]).get(projectId) || null;
}

async function listAssigneesWhere(column: 'job_card_id' | 'project_id', id: string): Promise<JobAssignee[]> {
  const { data, error } = await table('project_job_assignees')
    .select('job_card_id, project_id, profile_id, assigned_at')
    .eq(column, id)
    .order('assigned_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = (data || []) as Array<{
    job_card_id: string;
    project_id: string;
    profile_id: string;
    assigned_at: string;
  }>;
  const people = await profilesByIds(rows.map((row) => row.profile_id));
  return rows.map((row) => {
    const person = people.get(row.profile_id);
    return {
      job_card_id: row.job_card_id,
      project_id: row.project_id,
      profile_id: row.profile_id,
      assigned_at: row.assigned_at,
      full_name: person?.full_name || null,
      email: person?.email || null,
      role: person?.role || null,
    };
  });
}

export async function setJobAssignees(
  jobCardId: string,
  projectId: string,
  profileIds: string[],
  assignedBy: string
): Promise<void> {
  const unique = Array.from(new Set(profileIds.filter(Boolean)));
  const current = await listJobAssignees(jobCardId);
  const currentIds = current.map((row) => row.profile_id);
  const toAdd = unique.filter((id) => !currentIds.includes(id));
  const toRemove = currentIds.filter((id) => !unique.includes(id));

  if (toRemove.length > 0) {
    const { error } = await table('project_job_assignees')
      .delete()
      .eq('job_card_id', jobCardId)
      .in('profile_id', toRemove);
    if (error) throw new Error(error.message);
  }
  if (toAdd.length > 0) {
    const { error } = await table('project_job_assignees')
      .insert(toAdd.map((profile_id) => ({
        job_card_id: jobCardId,
        project_id: projectId,
        profile_id,
        assigned_by: assignedBy,
      })));
    if (error) throw new Error(error.message);
  }
}

/** Active Keystone subcontractors and CIS subcontractors from the directory. Office staff only. */
export async function listSubcontractorAccounts(): Promise<SubcontractorAccount[]> {
  const { data, error } = await table('directory_accounts')
    .select('id, company_name, contact_name, keystone_email')
    .eq('is_active', true)
    .or('is_keystone_subcontractor.eq.true,cis_band.neq.none');
  if (error) throw new Error(error.message);
  return ((data || []) as Array<{
    id: string;
    company_name: string | null;
    contact_name: string | null;
    keystone_email: string | null;
  }>)
    .map((row) => ({
      id: row.id,
      name: String(row.contact_name || '').trim() || String(row.company_name || '').trim() || 'Subcontractor',
      keystone_email: row.keystone_email ? row.keystone_email.toLowerCase() : null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Names of subcontractors without a login on job cards, for crew lists, by workspace or by card.
 * Empty when the table isn't there yet or the reader isn't office staff.
 */
export async function listJobSubcontractorNames(
  filter: { projectId: string } | { cardIds: string[] },
): Promise<Array<{ job_card_id: string; account_id: string; name: string }>> {
  if ('cardIds' in filter && !filter.cardIds.length) return [];
  const query = table('project_job_subcontractors')
    .select('job_card_id, account_id, assigned_at, account:directory_accounts(company_name, contact_name)')
    .order('assigned_at', { ascending: true });
  const { data, error } = await ('projectId' in filter
    ? query.eq('project_id', filter.projectId)
    : query.in('job_card_id', filter.cardIds));
  if (error) return [];
  return ((data || []) as Array<{
    job_card_id: string;
    account_id: string;
    account: { company_name: string | null; contact_name: string | null } | null;
  }>).map((row) => ({
    job_card_id: row.job_card_id,
    account_id: row.account_id,
    name: String(row.account?.contact_name || '').trim() || String(row.account?.company_name || '').trim() || 'Subcontractor',
  }));
}

/** Subcontractors without a login recorded on a job card (directory account ids). */
export async function listJobSubcontractorIds(jobCardId: string): Promise<string[]> {
  const { data, error } = await table('project_job_subcontractors')
    .select('account_id')
    .eq('job_card_id', jobCardId)
    .order('assigned_at', { ascending: true });
  if (error) throw new Error(error.message);
  return ((data || []) as Array<{ account_id: string }>).map((row) => row.account_id);
}

export async function setJobSubcontractors(
  jobCardId: string,
  projectId: string,
  accountIds: string[],
  assignedBy: string,
): Promise<void> {
  const unique = Array.from(new Set(accountIds.filter(Boolean)));
  const currentIds = await listJobSubcontractorIds(jobCardId);
  const toAdd = unique.filter((id) => !currentIds.includes(id));
  const toRemove = currentIds.filter((id) => !unique.includes(id));
  if (toRemove.length > 0) {
    const { error } = await table('project_job_subcontractors')
      .delete()
      .eq('job_card_id', jobCardId)
      .in('account_id', toRemove);
    if (error) throw new Error(error.message);
  }
  if (toAdd.length > 0) {
    const { error } = await table('project_job_subcontractors')
      .insert(toAdd.map((account_id) => ({
        job_card_id: jobCardId,
        project_id: projectId,
        account_id,
        assigned_by: assignedBy,
      })));
    if (error) throw new Error(error.message);
  }
}

/**
 * Subcontractors who worked this workspace's job cards: crew whose login is linked to a
 * directory account, plus subcontractors without a login. Office staff only.
 */
export async function listProjectSubcontractors(projectId: string): Promise<Array<{ accountId: string; name: string }>> {
  const [accounts, assignees, offApp] = await Promise.all([
    listSubcontractorAccounts(),
    listProjectJobAssignees(projectId),
    table('project_job_subcontractors').select('account_id').eq('project_id', projectId)
      .then(({ data, error }: { data: Array<{ account_id: string }> | null; error: { message: string } | null }) => {
        if (error) throw new Error(error.message);
        return (data || []).map((row) => row.account_id);
      }),
  ]);
  const emails = new Set(assignees.map((row) => String(row.email || '').toLowerCase()).filter(Boolean));
  return accounts
    .filter((account) => offApp.includes(account.id) || (!!account.keystone_email && emails.has(account.keystone_email)))
    .map((account) => ({ accountId: account.id, name: account.name }));
}

function asOrderAcceptanceCopy(row: Partial<OrderAcceptanceCopy> & Pick<OrderAcceptanceCopy, 'id' | 'project_id' | 'drive_item_id' | 'name' | 'storage_path' | 'copied_at'>): OrderAcceptanceCopy {
  return {
    id: row.id,
    project_id: row.project_id,
    drive_item_id: row.drive_item_id,
    name: row.name,
    storage_path: row.storage_path,
    content_type: row.content_type ?? null,
    copied_at: row.copied_at,
    redacted_name: row.redacted_name ?? null,
    redacted_storage_path: row.redacted_storage_path ?? null,
    redacted_content_type: row.redacted_content_type ?? null,
    redacted_at: row.redacted_at ?? null,
  };
}

export async function listOrderAcceptanceCopies(projectId: string): Promise<OrderAcceptanceCopy[]> {
  const { data } = await withOptionalColumns(OA_REDACT_COLUMNS, (present) => table('project_order_acceptance_files')
    .select([...OA_CORE_COLUMNS, ...present].join(', '))
    .eq('project_id', projectId)
    .order('name', { ascending: true }));
  return ((data || []) as Array<Partial<OrderAcceptanceCopy> & Pick<OrderAcceptanceCopy, 'id' | 'project_id' | 'drive_item_id' | 'name' | 'storage_path' | 'copied_at'>>)
    .map(asOrderAcceptanceCopy);
}

export async function listJobCardOrderAcceptanceFiles(projectId: string): Promise<JobCardOrderAcceptanceFile[]> {
  const { data, error } = await table('project_order_acceptance_files')
    .select('id, redacted_name, redacted_storage_path, redacted_content_type')
    .eq('project_id', projectId)
    .not('redacted_storage_path', 'is', null)
    .order('redacted_name', { ascending: true });
  if (error) {
    if (/redacted_name|redacted_storage_path|redacted_content_type/i.test(error.message)) return [];
    throw new Error(error.message);
  }
  return ((data || []) as Array<{
    id: string;
    redacted_name: string | null;
    redacted_storage_path: string | null;
    redacted_content_type: string | null;
  }>)
    .filter((row) => row.redacted_storage_path && row.redacted_name)
    .map((row) => ({
      id: row.id,
      name: String(row.redacted_name),
      storage_path: String(row.redacted_storage_path),
      content_type: row.redacted_content_type,
    }));
}

export async function syncOrderAcceptanceCopies(projectId: string, files: OrderAcceptanceFile[]): Promise<void> {
  const existing = await listOrderAcceptanceCopies(projectId);
  const keepIds = new Set(files.map((file) => file.id));
  const toRemove = existing.filter((row) => !keepIds.has(row.drive_item_id));

  for (const row of toRemove) {
    await directSupabase.storage.from(OA_BUCKET).remove([row.storage_path]);
    if (row.redacted_storage_path) {
      await directSupabase.storage.from(OA_REDACTED_BUCKET).remove([row.redacted_storage_path]);
    }
    const { error } = await table('project_order_acceptance_files')
      .delete()
      .eq('id', row.id);
    if (error) throw new Error(error.message);
  }

  for (const file of files) {
    const { blob, contentType } = await downloadDriveItemContent(file.id);
    const storagePath = `${projectId}/${file.id}/${storageSafeName(file.name)}`;
    const { error: uploadError } = await directSupabase.storage
      .from(OA_BUCKET)
      .upload(storagePath, blob, { contentType, upsert: true });
    if (uploadError) throw new Error(uploadError.message);
    const { error } = await table('project_order_acceptance_files')
      .upsert({
        project_id: projectId,
        drive_item_id: file.id,
        name: file.name,
        storage_path: storagePath,
        content_type: contentType,
        copied_at: new Date().toISOString(),
      }, { onConflict: 'project_id,drive_item_id' });
    if (error) throw new Error(error.message);
  }
}

export type OpenedOrderAcceptance = {
  url: string;
  name: string;
  contentType: string;
  kind: 'pdf' | 'image' | 'text' | 'other';
  text?: string;
};

function oaContentType(file: { name: string; content_type?: string | null }, blob: Blob): string {
  if (file.content_type && file.content_type !== 'application/octet-stream') return file.content_type;
  if (blob.type && blob.type !== 'application/octet-stream') return blob.type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.gif')) return 'image/gif';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.bmp')) return 'image/bmp';
  if (name.endsWith('.txt') || name.endsWith('.csv') || name.endsWith('.log')) return 'text/plain';
  return blob.type || 'application/octet-stream';
}

function oaPreviewKind(contentType: string, name: string): OpenedOrderAcceptance['kind'] {
  const type = contentType.toLowerCase();
  const lower = name.toLowerCase();
  if (type.includes('pdf') || lower.endsWith('.pdf')) return 'pdf';
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(lower)) return 'image';
  if (type.startsWith('text/') || /\.(txt|csv|md|log)$/i.test(lower)) return 'text';
  return 'other';
}

export function revokeOrderAcceptanceUrl(url: string) {
  URL.revokeObjectURL(url);
}

async function openStoredOrderAcceptance(
  bucket: string,
  file: { name: string; storage_path: string; content_type?: string | null },
): Promise<OpenedOrderAcceptance> {
  const { data, error } = await directSupabase.storage.from(bucket).download(file.storage_path);
  if (error || !data) throw new Error(error?.message || 'Could not open Order Acceptance file');
  const contentType = oaContentType(file, data);
  const typed = data.type === contentType ? data : new Blob([data], { type: contentType });
  const kind = oaPreviewKind(contentType, file.name);
  const url = URL.createObjectURL(typed);
  let text: string | undefined;
  if (kind === 'text') {
    text = await typed.text();
  }
  return { url, name: file.name, contentType, kind, text };
}

export async function downloadOrderAcceptanceOriginal(file: OrderAcceptanceCopy): Promise<Blob> {
  const { data, error } = await directSupabase.storage.from(OA_BUCKET).download(file.storage_path);
  if (error || !data) throw new Error(error?.message || 'Could not open the original Order Acceptance file');
  return data;
}

export async function openJobCardOrderAcceptance(file: JobCardOrderAcceptanceFile): Promise<OpenedOrderAcceptance> {
  return openStoredOrderAcceptance(OA_REDACTED_BUCKET, file);
}

export async function openOrderAcceptanceOriginal(file: OrderAcceptanceCopy): Promise<OpenedOrderAcceptance> {
  return openStoredOrderAcceptance(OA_BUCKET, file);
}

export async function openOrderAcceptanceRedacted(file: OrderAcceptanceCopy): Promise<OpenedOrderAcceptance> {
  if (!file.redacted_storage_path || !file.redacted_name) {
    throw new Error('No price-redacted copy is ready yet.');
  }
  return openStoredOrderAcceptance(OA_REDACTED_BUCKET, {
    name: file.redacted_name,
    storage_path: file.redacted_storage_path,
    content_type: file.redacted_content_type || file.content_type,
  });
}

function missingImagesTable(message?: string) {
  return !!message && /project_job_images|job-card-images/i.test(message);
}

async function signJobCardImagePaths(rows: JobCardImage[]): Promise<JobCardImage[]> {
  if (!rows.length) return rows;
  const { data, error } = await directSupabase.storage
    .from(JOB_CARD_IMAGES_BUCKET)
    .createSignedUrls(rows.map((row) => row.storage_path), 60 * 60);
  if (error) return rows.map((row) => ({ ...row, signedUrl: null }));
  const byPath = new Map((data || []).map((item) => [item.path, item.signedUrl || null]));
  return rows.map((row) => ({ ...row, signedUrl: byPath.get(row.storage_path) || null }));
}

export async function listJobCardImages(
  jobCardId: string,
  kind?: JobCardImageKind,
): Promise<JobCardImage[]> {
  let query = table('project_job_images')
    .select('id, job_card_id, project_id, comment_id, kind, storage_path, file_name, mime_type, uploaded_by, created_at')
    .eq('job_card_id', jobCardId)
    .order('created_at', { ascending: true });
  if (kind) query = query.eq('kind', kind);
  const { data, error } = await query;
  if (error) {
    if (missingImagesTable(error.message)) return [];
    throw new Error(error.message);
  }
  return signJobCardImagePaths((data || []) as JobCardImage[]);
}

export async function uploadJobCardImage(input: {
  jobCardId: string;
  projectId: string;
  authorId: string;
  kind: JobCardImageKind;
  commentId?: string | null;
  file: File;
  existingCount?: number;
}): Promise<JobCardImage> {
  if ((input.existingCount || 0) >= JOB_CARD_IMAGE_MAX_COUNT) {
    throw new Error(`You can attach up to ${JOB_CARD_IMAGE_MAX_COUNT} files here.`);
  }
  const invalid = isAllowedJobCardImage(input.file);
  if (invalid) throw new Error(invalid);
  if (input.kind === 'comment' && !input.commentId) {
    throw new Error('Save the comment before attaching media.');
  }
  const imageId = crypto.randomUUID();
  const storagePath = jobCardImageStoragePath({
    projectId: input.projectId,
    jobCardId: input.jobCardId,
    kind: input.kind,
    imageId,
    fileName: input.file.name,
    mimeType: input.file.type,
  });
  const uploaded = await directSupabase.storage
    .from(JOB_CARD_IMAGES_BUCKET)
    .upload(storagePath, input.file, {
      contentType: input.file.type || 'image/jpeg',
      upsert: false,
    });
  if (uploaded.error) {
    if (missingImagesTable(uploaded.error.message)) {
      throw new Error('Media storage is not set up yet. Apply the job-card-images migration.');
    }
    throw new Error(uploaded.error.message);
  }
  const { data, error } = await table('project_job_images')
    .insert({
      id: imageId,
      job_card_id: input.jobCardId,
      project_id: input.projectId,
      comment_id: input.kind === 'comment' ? input.commentId : null,
      kind: input.kind,
      storage_path: storagePath,
      file_name: sanitizeJobCardFileName(input.file.name),
      mime_type: input.file.type || 'image/jpeg',
      uploaded_by: input.authorId,
    })
    .select('id, job_card_id, project_id, comment_id, kind, storage_path, file_name, mime_type, uploaded_by, created_at')
    .single();
  if (error) {
    await directSupabase.storage.from(JOB_CARD_IMAGES_BUCKET).remove([storagePath]);
    if (missingImagesTable(error.message)) {
      throw new Error('Media storage is not set up yet. Apply the job-card-images migration.');
    }
    throw new Error(error.message);
  }
  const [signed] = await signJobCardImagePaths([data as JobCardImage]);
  return signed;
}

/**
 * Copies one card's survey photos onto another. Files are duplicated, not shared, because
 * deleting a card (or a photo) removes its files from storage.
 */
export async function copyJobCardSurveyImages(input: {
  fromJobCardId: string;
  toJobCardId: string;
  projectId: string;
  copiedBy: string;
}): Promise<number> {
  const images = await listJobCardImages(input.fromJobCardId, 'survey_notes');
  let copied = 0;
  for (const image of images) {
    const imageId = crypto.randomUUID();
    const storagePath = jobCardImageStoragePath({
      projectId: input.projectId,
      jobCardId: input.toJobCardId,
      kind: 'survey_notes',
      imageId,
      fileName: image.file_name || 'photo',
      mimeType: image.mime_type,
    });
    const { error: copyError } = await directSupabase.storage
      .from(JOB_CARD_IMAGES_BUCKET)
      .copy(image.storage_path, storagePath);
    if (copyError) throw new Error(copyError.message);
    const { error } = await table('project_job_images').insert({
      id: imageId,
      job_card_id: input.toJobCardId,
      project_id: input.projectId,
      comment_id: null,
      kind: 'survey_notes',
      storage_path: storagePath,
      file_name: image.file_name,
      mime_type: image.mime_type,
      uploaded_by: input.copiedBy,
    });
    if (error) {
      await directSupabase.storage.from(JOB_CARD_IMAGES_BUCKET).remove([storagePath]);
      throw new Error(error.message);
    }
    copied += 1;
  }
  return copied;
}

export async function deleteJobCardImage(image: Pick<JobCardImage, 'id' | 'storage_path'>): Promise<void> {
  await directSupabase.storage.from(JOB_CARD_IMAGES_BUCKET).remove([image.storage_path]);
  const { error } = await table('project_job_images').delete().eq('id', image.id);
  if (error && !missingImagesTable(error.message)) throw new Error(error.message);
}

export async function listJobComments(jobCardId: string): Promise<JobComment[]> {
  const { data, error } = await table('project_job_comments')
    .select('id, job_card_id, project_id, author_id, body, created_at')
    .eq('job_card_id', jobCardId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data || []) as Array<{
    id: string;
    job_card_id: string;
    project_id: string;
    author_id: string;
    body: string;
    created_at: string;
  }>;
  const images = await listJobCardImages(jobCardId, 'comment');
  const byComment = new Map<string, JobCardImage[]>();
  images.forEach((image) => {
    if (!image.comment_id) return;
    const list = byComment.get(image.comment_id) || [];
    list.push(image);
    byComment.set(image.comment_id, list);
  });
  const people = await profilesByIds(rows.map((row) => row.author_id));
  return rows.map((row) => {
    const person = people.get(row.author_id);
    return {
      id: row.id,
      job_card_id: row.job_card_id,
      project_id: row.project_id,
      author_id: row.author_id,
      body: row.body,
      created_at: row.created_at,
      author_name: person?.full_name || person?.email || null,
      images: byComment.get(row.id) || [],
    };
  });
}

export async function listProjectJobComments(projectId: string): Promise<Array<JobComment & { cardTitle: string }>> {
  const [cards, { data, error }] = await Promise.all([
    listProjectJobCards(projectId),
    table('project_job_comments')
      .select('id, job_card_id, project_id, author_id, body, created_at')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false }),
  ]);
  if (error) throw new Error(error.message);
  const rows = (data || []) as Array<{
    id: string;
    job_card_id: string;
    project_id: string;
    author_id: string;
    body: string;
    created_at: string;
  }>;
  const titleByCard = new Map(cards.map((card) => [card.id, card.title]));
  const people = await profilesByIds(rows.map((row) => row.author_id));
  return rows.map((row) => {
    const person = people.get(row.author_id);
    return {
      id: row.id,
      job_card_id: row.job_card_id,
      project_id: row.project_id,
      author_id: row.author_id,
      body: row.body,
      created_at: row.created_at,
      author_name: person?.full_name || person?.email || null,
      images: [],
      cardTitle: titleByCard.get(row.job_card_id) || 'Job card',
    };
  });
}

export async function addJobComment(
  jobCardId: string,
  projectId: string,
  authorId: string,
  body: string
): Promise<string | null> {
  const trimmed = body.trim();
  const { data, error } = await table('project_job_comments')
    .insert({
      job_card_id: jobCardId,
      project_id: projectId,
      author_id: authorId,
      body: trimmed,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  emitJobCardNotificationBackground({
    kind: 'comment',
    jobCardId,
    projectId,
    actorId: authorId,
    comment: trimmed || 'Added media',
  });
  return data?.id ? String(data.id) : null;
}

export async function updateJobComment(commentId: string, body: string): Promise<void> {
  const { error } = await table('project_job_comments')
    .update({ body: body.trim() })
    .eq('id', commentId);
  if (error) throw new Error(error.message);
}

export async function deleteJobComment(commentId: string): Promise<void> {
  const { data } = await table('project_job_images')
    .select('storage_path')
    .eq('comment_id', commentId);
  const paths = ((data || []) as Array<{ storage_path: string }>).map((row) => row.storage_path).filter(Boolean);
  if (paths.length) {
    await directSupabase.storage.from(JOB_CARD_IMAGES_BUCKET).remove(paths);
  }
  const { error } = await table('project_job_comments')
    .delete()
    .eq('id', commentId);
  if (error) throw new Error(error.message);
}

const COMPLETION_CORE_COLUMNS = ['id', 'job_card_id', 'project_id', 'attested_by', 'attested_at', 'job_complete', 'customer_satisfaction_form_issued'];
const COMPLETION_OPTIONAL_COLUMNS = ['action', 'end_reason', 'continued_on_card_id'];

export async function listJobCompletionEvents(jobCardId: string): Promise<JobCompletionEvent[]> {
  const { data } = await withOptionalColumns(COMPLETION_OPTIONAL_COLUMNS, (present) => table('project_job_completions')
    .select([...COMPLETION_CORE_COLUMNS, ...present].join(', '))
    .eq('job_card_id', jobCardId)
    .order('attested_at', { ascending: true }));
  const rows = (data || []) as Array<{
    id: string;
    job_card_id?: string;
    project_id: string;
    attested_by: string;
    attested_at: string;
    job_complete: boolean;
    customer_satisfaction_form_issued: boolean;
    action?: string | null;
    end_reason?: string | null;
    continued_on_card_id?: string | null;
  }>;
  const people = await profilesByIds(rows.map((row) => row.attested_by));
  return rows.map((row) => {
    const person = people.get(row.attested_by);
    const action = row.action === 'ended'
      ? 'ended' as const
      : row.action === 'released' || (row.action == null && row.job_complete === false)
        ? 'released' as const
        : 'completed' as const;
    return {
      id: row.id,
      job_card_id: row.job_card_id || jobCardId,
      project_id: row.project_id,
      action,
      attested_by: row.attested_by,
      attested_at: row.attested_at,
      job_complete: row.job_complete,
      customer_satisfaction_form_issued: row.customer_satisfaction_form_issued,
      actor_name: person?.full_name || person?.email || null,
      end_reason: row.end_reason ?? null,
      continued_on_card_id: row.continued_on_card_id ?? null,
    };
  });
}

export function currentJobCompletion(events: JobCompletionEvent[]): JobCompletionEvent | null {
  const latest = events[events.length - 1];
  return latest?.action === 'completed' ? latest : null;
}

/** The card is closed: marked complete, or ended and continued on a later card. A later release reopens it. */
export function currentJobCardClosure(events: JobCompletionEvent[]): JobCompletionEvent | null {
  const latest = events[events.length - 1];
  return latest?.action === 'completed' || latest?.action === 'ended' ? latest : null;
}

/** Every card is closed and at least one was marked complete (ended cards hand the job on, they don't finish it). */
export async function areAllProjectJobCardsComplete(projectId: string): Promise<boolean> {
  const cards = await listProjectJobCards(projectId);
  if (cards.length === 0) return false;
  const closures = await Promise.all(cards.map(async (card) => (
    currentJobCardClosure(await listJobCompletionEvents(card.id))
  )));
  return closures.every(Boolean) && closures.some((closure) => closure?.action === 'completed');
}

export async function recordJobCompletion(jobCardId: string, projectId: string, attestedBy: string): Promise<void> {
  const { error } = await table('project_job_completions')
    .insert({
      job_card_id: jobCardId,
      project_id: projectId,
      attested_by: attestedBy,
      attested_at: new Date().toISOString(),
      job_complete: true,
      customer_satisfaction_form_issued: true,
      action: 'completed',
    });
  if (error) throw new Error(error.message);
  emitActivityEventBackground({
    action: 'job.completed',
    entityType: 'job',
    entityId: jobCardId,
    projectId,
    actorId: attestedBy,
    label: 'Job marked complete',
  });
  emitJobCardNotificationBackground({
    kind: 'completed',
    jobCardId,
    projectId,
    actorId: attestedBy,
  });
}

export async function recordJobRelease(jobCardId: string, projectId: string, releasedBy: string): Promise<void> {
  const { error } = await table('project_job_completions')
    .insert({
      job_card_id: jobCardId,
      project_id: projectId,
      attested_by: releasedBy,
      attested_at: new Date().toISOString(),
      job_complete: false,
      customer_satisfaction_form_issued: false,
      action: 'released',
    });
  if (error) throw new Error(error.message);
  emitActivityEventBackground({
    action: 'job.released',
    entityType: 'job',
    entityId: jobCardId,
    projectId,
    actorId: releasedBy,
    label: 'Job released',
  });
  emitJobCardNotificationBackground({
    kind: 'reopened',
    jobCardId,
    projectId,
    actorId: releasedBy,
  });
}

/** Office only: close a card that stops part-way (delay, crew change). The job carries on under a later card. */
export async function recordJobEnded(input: {
  jobCardId: string;
  projectId: string;
  endedBy: string;
  reason: string;
  continuedOnCardId: string;
}): Promise<void> {
  const { error } = await table('project_job_completions')
    .insert({
      job_card_id: input.jobCardId,
      project_id: input.projectId,
      attested_by: input.endedBy,
      attested_at: new Date().toISOString(),
      job_complete: false,
      customer_satisfaction_form_issued: false,
      action: 'ended',
      end_reason: input.reason,
      continued_on_card_id: input.continuedOnCardId,
    });
  if (error) throw new Error(error.message);
  emitActivityEventBackground({
    action: 'job.ended',
    entityType: 'job',
    entityId: input.jobCardId,
    projectId: input.projectId,
    actorId: input.endedBy,
    label: 'Job card ended',
    metadata: { reason: input.reason, continuedOnCardId: input.continuedOnCardId },
  });
}

export async function listAssignedJobs(profileId: string): Promise<AssignedJob[]> {
  const { data, error } = await table('project_job_assignees')
    .select('job_card_id, project_id, assigned_at')
    .eq('profile_id', profileId)
    .order('assigned_at', { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data || []) as Array<{ job_card_id: string; project_id: string; assigned_at: string }>;
  const seen = new Set<string>();
  const unique = rows.filter((row) => {
    if (seen.has(row.job_card_id)) return false;
    seen.add(row.job_card_id);
    return true;
  });
  const projectIds = unique.map((row) => row.project_id);
  const cardIds = unique.map((row) => row.job_card_id);
  const cards: Array<{
    id: string;
    title: string | null;
    install_date_override?: string | null;
    install_end_date_override?: string | null;
    follows_booking?: boolean | null;
  }> = [];
  const projects: Array<{
    id: string;
    project_name: string | null;
    customer_name: string | null;
    install_date: string | null;
    supply_only: boolean | null;
    metadata?: unknown;
    site_address?: string | null;
  }> = [];
  if (cardIds.length > 0) {
    const { data: cardRows } = await withOptionalColumns(INSTALL_OVERRIDE_COLUMNS, (present) => table('project_job_cards')
      .select(['id', 'title', ...present].join(', '))
      .in('id', cardIds));
    cards.push(...((cardRows || []) as typeof cards));
  }
  if (projectIds.length > 0) {
    const { data: projectRows, error: projectError } = await directSupabase
      .from('projects')
      .select('id, project_name, customer_name, install_date, supply_only, metadata, customer_id')
      .in('id', projectIds);
    if (projectError) throw new Error(projectError.message);
    const projectList = (projectRows || []) as Array<{
      id: string;
      project_name: string | null;
      customer_name: string | null;
      install_date: string | null;
      supply_only: boolean | null;
      metadata?: unknown;
      customer_id?: string | number | null;
    }>;
    const customerIds = Array.from(new Set(
      projectList.map((row) => String(row.customer_id || '').trim()).filter(Boolean)
    ));
    const addressByCustomer = new Map<string, string>();
    if (customerIds.length > 0) {
      const { data: customerRows, error: customerError } = await directSupabase
        .from('customers')
        .select('id, address')
        .in('id', customerIds);
      if (!customerError) {
        ((customerRows || []) as Array<{ id: string | number; address?: string | null }>).forEach((row) => {
          addressByCustomer.set(String(row.id), String(row.address || '').trim());
        });
      }
    }
    projects.push(...projectList.map((row) => ({
      ...row,
      site_address: addressByCustomer.get(String(row.customer_id || '')) || null,
    })));
  }
  let events: InstallEventRow[] = [];
  if (projectIds.length > 0) {
    const { data: eventRows, error: eventError } = await directSupabase
      .from('project_events')
      .select('project_id, event_date, end_date, metadata, status, created_at')
      .in('project_id', projectIds)
      .eq('event_type', 'installationDateConfirmation');
    if (eventError) throw new Error(eventError.message);
    events = (eventRows || []) as unknown as InstallEventRow[];
  }
  let completions: CompletionRow[] = [];
  if (cardIds.length > 0) {
    const { data: completionRows } = await withOptionalColumns(COMPLETION_OPTIONAL_COLUMNS, (present) => table('project_job_completions')
      .select(['job_card_id', 'attested_at', 'job_complete', ...present].join(', '))
      .in('job_card_id', cardIds));
    completions = ((completionRows || []) as Array<{ job_card_id: string; attested_at: string; job_complete: boolean; action?: string | null }>)
      .map((row) => ({
        job_card_id: row.job_card_id,
        attested_at: row.attested_at,
        action: row.action === 'ended'
          ? 'ended' as const
          : row.action === 'released' || (row.action == null && row.job_complete === false)
            ? 'released' as const
            : 'completed' as const,
      }));
  }
  return assembleAssignedJobs({
    assigneeRows: unique,
    cards,
    projects,
    events,
    completions,
  });
}
